import { CsvConfigRow } from '@/components/user/csv-analysis/CsvConfigUpload';

// Sanitize and normalize ID string for comparison
const sanitizeId = (id: string): string => {
  if (!id) return '';
  
  return id
    .replace(/[^\x20-\x7E]/g, '') // Remove non-printable characters
    .replace(/\s*-\s*/g, '-') // Standardize hyphens with no spaces
    .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
    .trim();
};

// Normalize filename by removing extensions and common suffixes
const normalizeFilename = (filename: string): string => {
  return filename
    .replace(/\.xlsx?\s*-\s*List of trades\.csv$/i, '') // Remove "- List of trades.csv"
    .replace(/\.(xlsx?|xls|csv)$/i, '') // Remove file extensions
    .trim();
};

interface ParsedConfig {
  symbol: string;
  suffix: string; // .P, etc.
  tp: number;
  sl: number;
  timeframe: string;
  originalId: string;
}

/**
 * Parse a config ID or filename into structured components with enhanced accuracy
 */
export const parseConfigId = (id: string): ParsedConfig => {
  const sanitized = sanitizeId(normalizeFilename(id));
  if (!sanitized) {
    return {
      symbol: '',
      suffix: '',
      tp: 0,
      sl: 0,
      timeframe: '',
      originalId: id
    };
  }

  // Enhanced regex patterns for different formats with better decimal support
  const patterns = [
    // Pattern 1: SYMBOL.P-TP-SL-TIMEFRAME (e.g., APTUSDT.P-1.3-2.6-1M)
    /^([A-Z]{3,8})(\.[A-Z])?-(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)-(\w+)$/i,
    // Pattern 2: SYMBOL.P-TP-TIMEFRAME (e.g., ETHUSDT.P-2.1-5S) - SL defaults to 2x TP
    /^([A-Z]{3,8})(\.[A-Z])?-(\d+(?:\.\d+)?)-(\w+)$/i,
    // Pattern 3: SYMBOL-TP-SL-TIMEFRAME (no .P suffix)
    /^([A-Z]{3,8})-(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)-(\w+)$/i,
    // Pattern 4: SYMBOL-TP-TIMEFRAME (no .P suffix, no SL)
    /^([A-Z]{3,8})-(\d+(?:\.\d+)?)-(\w+)$/i,
    // Pattern 5: Alternative with spaces: SYMBOL .P TP SL TIMEFRAME
    /^([A-Z]{3,8})\s*(\.[A-Z])?\s*(\d+(?:\.\d+)?)\s*(\d+(?:\.\d+)?)?\s*(\w+)$/i,
  ];

  for (const pattern of patterns) {
    const match = sanitized.match(pattern);
    if (match) {
      let symbol, suffix, tp, sl, timeframe;
      
      if (match.length === 6) {
        // Pattern with SL
        [, symbol, suffix = '', tp, sl, timeframe] = match;
      } else if (match.length === 5) {
        // Pattern without SL
        [, symbol, suffix = '', tp, timeframe] = match;
        sl = undefined;
      }
      
      const parsedTp = parseFloat(tp);
      const parsedSl = sl ? parseFloat(sl) : parsedTp * 2; // Default SL = 2x TP
      
      return {
        symbol: symbol.toUpperCase(),
        suffix: suffix ? suffix.toUpperCase() : '',
        tp: parsedTp,
        sl: parsedSl,
        timeframe: timeframe.toUpperCase(),
        originalId: id
      };
    }
  }

  // Enhanced fallback: try to extract symbol and numbers
  const symbolMatch = sanitized.match(/^([A-Z]{3,8})/i);
  const numberMatches = sanitized.match(/(\d+(?:\.\d+)?)/g);
  const timeframeMatch = sanitized.match(/([0-9]+[SMH]|[0-9]+M)$/i);
  
  if (symbolMatch) {
    const symbol = symbolMatch[1].toUpperCase();
    const suffix = sanitized.includes('.P') ? '.P' : '';
    const tp = numberMatches && numberMatches[0] ? parseFloat(numberMatches[0]) : 0;
    const sl = numberMatches && numberMatches[1] ? parseFloat(numberMatches[1]) : (tp > 0 ? tp * 2 : 0);
    const timeframe = timeframeMatch ? timeframeMatch[1].toUpperCase() : '';
    
    return {
      symbol,
      suffix,
      tp,
      sl,
      timeframe,
      originalId: id
    };
  }

  return {
    symbol: '',
    suffix: '',
    tp: 0,
    sl: 0,
    timeframe: '',
    originalId: id
  };
};

/**
 * Compare two parsed configs and return a match score (0-100) with weighted criteria
 */
export const compareParsed = (fileParsed: ParsedConfig, configParsed: ParsedConfig): { score: number; reason: string } => {
  let score = 0;
  const reasons: string[] = [];
  
  // Symbol matching (most important - 50 points for exact match)
  if (fileParsed.symbol === configParsed.symbol) {
    score += 50;
    reasons.push('symbol exact');
  } else if (fileParsed.symbol && configParsed.symbol) {
    // Check substring matching for fallback
    if (fileParsed.symbol.includes(configParsed.symbol) || configParsed.symbol.includes(fileParsed.symbol)) {
      score += 20;
      reasons.push('symbol partial');
    } else {
      return { score: 0, reason: 'symbol mismatch' };
    }
  } else {
    return { score: 0, reason: 'missing symbol' };
  }
  
  // Suffix matching (15 points)
  if (fileParsed.suffix === configParsed.suffix) {
    score += 15;
    reasons.push('suffix match');
  } else if (!fileParsed.suffix && !configParsed.suffix) {
    score += 15;
    reasons.push('no suffix both');
  } else {
    // Partial credit for suffix tolerance
    score += 5;
    reasons.push('suffix diff');
  }
  
  // TP matching (15 points)
  if (fileParsed.tp > 0 && configParsed.tp > 0) {
    if (Math.abs(fileParsed.tp - configParsed.tp) < 0.01) {
      score += 15;
      reasons.push('TP exact');
    } else if (Math.abs(fileParsed.tp - configParsed.tp) < 0.5) {
      score += 10;
      reasons.push('TP close');
    } else {
      score += 3;
      reasons.push('TP diff');
    }
  } else if (fileParsed.tp === 0 && configParsed.tp === 0) {
    score += 10;
    reasons.push('no TP both');
  }
  
  // SL matching (10 points)
  if (fileParsed.sl > 0 && configParsed.sl > 0) {
    if (Math.abs(fileParsed.sl - configParsed.sl) < 0.01) {
      score += 10;
      reasons.push('SL exact');
    } else if (Math.abs(fileParsed.sl - configParsed.sl) < 0.5) {
      score += 7;
      reasons.push('SL close');
    } else {
      score += 2;
      reasons.push('SL diff');
    }
  }
  
  // Timeframe matching (10 points)
  if (fileParsed.timeframe && configParsed.timeframe) {
    if (fileParsed.timeframe === configParsed.timeframe) {
      score += 10;
      reasons.push('timeframe exact');
    } else {
      score += 2;
      reasons.push('timeframe diff');
    }
  } else if (!fileParsed.timeframe && !configParsed.timeframe) {
    score += 5;
    reasons.push('no timeframe both');
  }
  
  return { score, reason: reasons.join(', ') };
};

/**
 * Extract base ID from filename for matching with CSV config
 */
export const extractFileId = (filename: string): string => {
  const parsed = parseConfigId(filename);
  
  let id = parsed.symbol;
  if (parsed.suffix) id += parsed.suffix;
  if (parsed.tp > 0) {
    id += `-${parsed.tp}`;
    if (parsed.sl > 0 && Math.abs(parsed.sl - parsed.tp * 2) > 0.01) {
      id += `-${parsed.sl}`;
    }
  }
  if (parsed.timeframe) id += `-${parsed.timeframe}`;
  
  return id;
};

/**
 * Find matching config with symbol-first approach and fallback logic
 */
export const findMatchingConfig = (filename: string, configs: CsvConfigRow[]): CsvConfigRow | null => {
  if (!configs || configs.length === 0) return null;
  
  const fileParsed = parseConfigId(filename);
  
  // Create symbol-based index for efficient lookup
  const configsBySymbol = new Map<string, CsvConfigRow[]>();
  configs.forEach(config => {
    const configParsed = parseConfigId(config.id);
    if (configParsed.symbol) {
      if (!configsBySymbol.has(configParsed.symbol)) {
        configsBySymbol.set(configParsed.symbol, []);
      }
      configsBySymbol.get(configParsed.symbol)!.push(config);
    }
  });
  
  let bestMatch: CsvConfigRow | null = null;
  let bestScore = 0;
  
  // Debug logging
  console.log(`🔍 Finding match for filename: ${filename}`);
  console.log(`📝 Parsed file data:`, fileParsed);
  console.log(`🗂️ Symbol-based configs available:`, Array.from(configsBySymbol.keys()));
  
  // Pass 1: Symbol-first matching (exact symbol match)
  if (fileParsed.symbol && configsBySymbol.has(fileParsed.symbol)) {
    const sameSymbolConfigs = configsBySymbol.get(fileParsed.symbol)!;
    console.log(`🎯 Pass 1: Checking ${sameSymbolConfigs.length} configs with same symbol: ${fileParsed.symbol}`);
    
    for (const config of sameSymbolConfigs) {
      const configParsed = parseConfigId(config.id);
      const comparison = compareParsed(fileParsed, configParsed);
      
      console.log(`  📋 Config: ${config.id} → Score: ${comparison.score}% (${comparison.reason})`);
      
      if (comparison.score > bestScore) {
        bestScore = comparison.score;
        bestMatch = config;
      }
    }
    
    // Lower threshold for same symbol matches
    const symbolThreshold = 50;
    if (bestScore >= symbolThreshold) {
      console.log(`✅ Symbol match found: ${bestMatch?.id} (${bestScore}%)`);
      return bestMatch;
    }
  }
  
  // Pass 2: Fallback to substring matching if no exact symbol match
  if (fileParsed.symbol && bestScore < 50) {
    console.log(`🔄 Pass 2: Fallback substring matching for symbol: ${fileParsed.symbol}`);
    
    for (const config of configs) {
      const configParsed = parseConfigId(config.id);
      
      // Check if config symbol is a substring of file symbol or vice versa
      const isSubstring = fileParsed.symbol.includes(configParsed.symbol) || 
                         configParsed.symbol.includes(fileParsed.symbol);
      
      if (isSubstring && configParsed.symbol.length >= 3) {
        const comparison = compareParsed(fileParsed, configParsed);
        console.log(`  📋 Substring Config: ${config.id} → Score: ${comparison.score}% (${comparison.reason})`);
        
        if (comparison.score > bestScore) {
          bestScore = comparison.score;
          bestMatch = config;
        }
      }
    }
  }
  
  // Final threshold check
  const finalThreshold = 40; // Lower threshold for fallback matches
  if (bestScore >= finalThreshold) {
    console.log(`✅ Best match: ${bestMatch?.id} (${bestScore}%)`);
    return bestMatch;
  } else {
    console.log(`❌ No match found (best score: ${bestScore}% < ${finalThreshold}%)`);
    return null;
  }
};

/**
 * Get detailed matching information for debugging with enhanced context
 */
export const getMatchingDetails = (filename: string, configs: CsvConfigRow[]) => {
  const fileParsed = parseConfigId(filename);
  const allMatches: Array<{
    config: CsvConfigRow;
    parsed: ParsedConfig;
    score: number;
    reason: string;
  }> = [];

  // Create symbol-based grouping for debug info
  const configsBySymbol = new Map<string, CsvConfigRow[]>();
  configs.forEach(config => {
    const configParsed = parseConfigId(config.id);
    if (configParsed.symbol) {
      if (!configsBySymbol.has(configParsed.symbol)) {
        configsBySymbol.set(configParsed.symbol, []);
      }
      configsBySymbol.get(configParsed.symbol)!.push(config);
    }
  });

  // Count configs for the same symbol
  const sameSymbolCount = fileParsed.symbol ? (configsBySymbol.get(fileParsed.symbol)?.length || 0) : 0;

  // Score all configurations
  for (const config of configs) {
    const configParsed = parseConfigId(config.id);
    const comparison = compareParsed(fileParsed, configParsed);
    
    allMatches.push({
      config,
      parsed: configParsed,
      score: comparison.score,
      reason: comparison.reason
    });
  }

  // Sort by score descending
  allMatches.sort((a, b) => b.score - a.score);

  // Determine best match with intelligent threshold
  let bestMatch: CsvConfigRow | null = null;
  if (allMatches.length > 0) {
    const topScore = allMatches[0].score;
    if (sameSymbolCount > 0 && topScore >= 40) {
      // Lower threshold if we have same symbol configs
      bestMatch = allMatches[0].config;
    } else if (topScore >= 60) {
      // Higher threshold for cross-symbol matches
      bestMatch = allMatches[0].config;
    }
  }

  return {
    fileParsed,
    sanitizedId: sanitizeId(normalizeFilename(filename)),
    sameSymbolCount,
    availableSymbols: Array.from(configsBySymbol.keys()),
    allMatches: allMatches.slice(0, 3), // Top 3
    bestMatch,
    confidence: allMatches.length > 0 ? allMatches[0].score : 0
  };
};

/**
 * Calculate which position sizes to select based on Martingale logic
 */
export const calculatePositionSelection = (
  positionSizes: string[], 
  startAfter: number, 
  stopAfter: number
): string[] => {
  if (!positionSizes || positionSizes.length === 0) {
    return [];
  }
  
  // Sort position sizes numerically to ensure proper Martingale sequence
  const sortedSizes = [...positionSizes].sort((a, b) => parseFloat(a) - parseFloat(b));
  
  const selectedSizes: string[] = [];
  
  // Convert 1-based loss numbers to 0-based array indices
  for (let lossIndex = startAfter; lossIndex < stopAfter && lossIndex < sortedSizes.length; lossIndex++) {
    selectedSizes.push(sortedSizes[lossIndex]);
  }
  
  return selectedSizes;
};

/**
 * Validate if position sizes follow Martingale pattern
 */
export const validateMartingalePattern = (positionSizes: string[]): {
  isValid: boolean;
  message: string;
} => {
  if (positionSizes.length < 2) {
    return { isValid: true, message: 'Not enough positions to validate pattern' };
  }
  
  const sorted = [...positionSizes].sort((a, b) => parseFloat(a) - parseFloat(b));
  const numbers = sorted.map(s => parseFloat(s));
  
  for (let i = 1; i < numbers.length; i++) {
    const ratio = numbers[i] / numbers[i - 1];
    if (ratio < 1.5 || ratio > 3) {
      return {
        isValid: false,
        message: `Position size ratio ${ratio.toFixed(2)}x is outside expected Martingale range (1.5x - 3x)`
      };
    }
  }
  
  return { isValid: true, message: 'Valid Martingale progression detected' };
};